'use client';

/**
 * FormPopup — 리드폼 팝업 쉘 (탭형 · 다중선택형 · 단일 폼)
 * ─────────────────────────────────────────────────────────────
 *
 * ## 1. 마운트 
 * (publishing)/layout.tsx
 *   <FormPopup />
 * 마운트 후 아래 두 방식 모두 사용 가능합니다.
 *
 * ─────────────────────────────────────────────────────────────
 * ## 2. 여는 방법
 *
 * ### A. Button `action` (페이지·컴포넌트에서 가장 많이 씀)
 *
 * import { Button } from '@publishing/components/ui/Button';
 * import { FORM_KEY } from '@publishing/modules/forms';
 *
 * | 용도           | action 예시 |
 * |----------------|-------------|
 * | 단일 폼 (기본) | `{ key: FORM_KEY.PROMOTION }` |
 * | 탭형           | `{ key: FORM_KEY.SHARED_OFFICE, openType: 'tab' }` |
 * | 다중 선택      | `{ openType: 'multi', keys: [FORM_KEY.SHARED_OFFICE] }` |
 *
 * - `key`는 FORM_KEY 프리셋 (_config.tsx FORM_MENUS). 필드·productType 자동 연결.
 * - tab·multi 메뉴는 CONSULT_POPUP_GROUPS 기준 (PROMOTION 등 single 전용 제외).
 * - `openType` 생략 = single. 명시하려면 `openType: 'single'`.
 * - 이벤트·1회성: `FORM_KEY.PROMOTION` (기본 4필드).
 *
 *   <Button action={{ key: FORM_KEY.PROMOTION }}>상담 신청</Button>
 *   <Button action={{ key: FORM_KEY.SHARED_OFFICE, openType: 'tab' }}>상품 선택 상담</Button>
 *   <Button action={{ openType: 'multi' }}>복수 상품 상담</Button>
 *   <Button action={{ openType: 'multi', keys: [FORM_KEY.SHARED_OFFICE] }}>복수 상품 (미리 선택)</Button>
 *
 * ### B. 함수 직접 호출 (onClick·useEffect 등)
 *
 * import { openPopup, openPopupTab, openPopupMulti, FORM_KEY } from '@publishing/modules/forms';
 *
 *   openPopup(FORM_KEY.PROMOTION);              // 단일 폼
 *   openPopupTab(FORM_KEY.SHARED_OFFICE);        // 탭형 (해당 탭 선택 상태)
 *   openPopupMulti();                                // 다중 선택 (빈 상태)
 *   openPopupMulti([FORM_KEY.SHARED_OFFICE]);   // 다중 선택 (미리 체크)
 *
 * Button action 내부에서도 위 함수들을 호출합니다.
 *
 * ─────────────────────────────────────────────────────────────
 * ## 3. 모드별 UI
 *
 * - **single** — 좌측 메뉴 없음. `key`에 해당하는 폼만 표시. 헤더는 메뉴 label.
 * - **tab**    — 좌측 상품 탭 + 폼. `key`로 초기 선택 탭 지정.
 * - **multi**  — 상품 다중 선택 후 한 번에 제출. POPUP_FORM_NAME_MULTI 사용.
 *
 * ─────────────────────────────────────────────────────────────
 * ## 4. 커스텀 1회성 팝업 (FormPopup 밖 — Button `type: 'popup'`)
 *
 * 프리셋 key만으로 부족할 때. CommonPopup + FormLayerPopup 셸.
 * 페이지에 CommonPopupProvider 필요 (이벤트 섹션 등).
 *
 * import { FormPopupContent, FORM_KEY } from '@publishing/modules/forms';
 *
 *   <Button action={{
 *     type: 'popup',
 *     id: 'my-event-form',
 *     title: '원하는 팝업 제목',
 *     content: <FormPopupContent type={FORM_KEY.PROMOTION} />,
 *   }} />
 *
 * `fields`·`submitLabel` 등은 FormPopupContent props로 커스텀.
 *
 * ─────────────────────────────────────────────────────────────
 * ## 5. submit 후 동작 (선택)
 *
 * openPopup / openPopupTab / openPopupMulti 두 번째 인수:
 *
 *   openPopup(FORM_KEY.PROMOTION, {
 *     submitAction: { type: 'redirect', href: '/submit/download' },
 *   });
 *
 * 미지정 시 팝업 안에서 완료 화면 → 닫기.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LeadFormName } from '@/features/lead-form/constants/formName';
import { FormModeContext } from './_context';
import clsx from 'clsx';
import { Button } from '@publishing/components/ui/Button';
import Icon from '@publishing/components/ui/Icon';
import { ActionCloseType1 } from '@publishing/components/ui/SvgSprite';
import Form from './Form';
import FormConsultBody from './common/FormConsultBody';
import PopupDefault from '../submit/PopupDefault';
import { CONSULT_POPUP_MENUS, FORM_MENUS, POPUP_FORM_NAME, POPUP_LEAD_TYPE, POPUP_TITLE } from './_config';
import type { FormAgreementOptions, FormMenuConfig, FormKey, SubmitAction, FormActionFormName } from './_types';
import { resolveFormActionFormName } from './_types';
import { fetchLeadFormRecommendations } from '../submit/_config';
import type { BlogArticle } from '../submit/_types';
import { useBodyScrollLock } from '@publishing/hooks/useBodyScrollLock';
import styles from './FormPopup.module.scss';

// ── 팝업 내부 상태 타입 ───────────────────────────────────

type PopupVariant = 'tab' | 'multi' | 'single';

interface PopupState {
  variant: PopupVariant;
  keys: FormKey[];
  formName?: LeadFormName;
  /** 팝업을 연 CTA 의 리드폼 구분값 — 제출 이벤트까지 이어 붙인다. */
  leadFormNum?: number;
  submitAction?: SubmitAction;
}

export type OpenPopupOptions = {
  submitAction?: SubmitAction;
  /** LEAD_FORM_NAME.PROMOTION 또는 'PROMOTION' — 미지정 시 POPUP_FORM_NAME */
  formName?: FormActionFormName;
  /** 한 페이지에 리드폼이 여럿일 때의 구분값(1-based) — 오픈 CTA 가 부여한 값을 그대로 잇는다. */
  leadFormNum?: number;
};

function resolveOpenOptions(
  options?: OpenPopupOptions,
): Pick<PopupState, 'submitAction' | 'formName' | 'leadFormNum'> {
  if (!options) return {};
  return {
    submitAction: options.submitAction,
    formName: options.formName ? resolveFormActionFormName(options.formName) : undefined,
    leadFormNum: options.leadFormNum,
  };
}

// ── 전역 오프너 ───────────────────────────────────────────

let _open: ((state: PopupState) => void) | null = null;

function warn() {
  console.warn('[FormPopup] <FormPopup />이 마운트되지 않았습니다.');
}

/** 탭형 — 특정 탭이 선택된 상태로 팝업 열기 */
export function openPopupTab(key: FormKey, options?: OpenPopupOptions) {
  if (!_open) return warn();
  _open({ variant: 'tab', keys: [key], ...resolveOpenOptions(options) });
}

/** 다중선택형 — 선택 없거나 1개 이상 미리 선택된 상태로 열기 */
export function openPopupMulti(keys?: FormKey[], options?: OpenPopupOptions) {
  if (!_open) return warn();
  _open({ variant: 'multi', keys: keys ?? [], ...resolveOpenOptions(options) });
}

/** 단일 폼 — 메뉴 없이 바로 폼 노출 */
export function openPopup(key: FormKey, options?: OpenPopupOptions) {
  if (!_open) return warn();
  _open({ variant: 'single', keys: [key], ...resolveOpenOptions(options) });
}

// ── Props ─────────────────────────────────────────────────

export interface FormPopupProps {
  menus?: FormMenuConfig[];
  title?: string;
  agreement?: FormAgreementOptions;
}

// ── 컴포넌트 ──────────────────────────────────────────────

export default function FormPopup({
  menus = CONSULT_POPUP_MENUS,
  title = POPUP_TITLE,
  agreement,
}: FormPopupProps) {
  const [popupState, setPopupState] = useState<PopupState | null>(null);
  const [step, setStep] = useState<'form' | 'complete'>('form');
  const [completeKey, setCompleteKey] = useState('default');
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [hasLoadedArticles, setHasLoadedArticles] = useState(false);

  const isOpen = popupState !== null;

  const resetState = useCallback(() => {
    setStep('form');
    setCompleteKey('default');
  }, []);

  const handleClose = useCallback(() => {
    setPopupState(null);
    resetState();
  }, [resetState]);

  const openImperative = useCallback(
    (incoming: PopupState) => {
      setPopupState(incoming);
      resetState();
    },
    [resetState],
  );

  useEffect(() => {
    _open = openImperative;
    return () => { _open = null; };
  }, [openImperative]);

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (step !== 'complete' || hasLoadedArticles) return;

    let ignore = false;

    fetchLeadFormRecommendations().then((nextArticles) => {
      if (ignore) return;
      setArticles(nextArticles);
      setHasLoadedArticles(true);
    });

    return () => {
      ignore = true;
    };
  }, [step, hasLoadedArticles]);

  const heading = useMemo(() => {
    if (step === 'complete') return '상담 신청 완료';
    if (popupState?.variant === 'single') {
      return FORM_MENUS.find((m) => m.key === popupState.keys[0])?.label ?? title;
    }
    return title;
  }, [step, popupState, title]);

  const singleMenu = useMemo(
    () => (popupState?.variant === 'single' ? FORM_MENUS.find((m) => m.key === popupState.keys[0]) : undefined),
    [popupState],
  );

  if (!isOpen) return null;

  const { variant, keys, formName: popupFormName, leadFormNum } = popupState!;

  return (
    <section
      className={styles.overlay}
      aria-label="상담 문의"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={clsx(styles.popup, styles[`popup_${variant}`])}>
        <div className={styles.head}>
          <strong>{heading}</strong>
          <Button
            type="black"
            theme="icon"
            filled
            size={[32, 24]}
            bgColor="var(--color-alpha-white-5)"
            onClick={handleClose}
            className={styles.close_button}
            aria-label="팝업 닫기"
          >
            <Icon name={ActionCloseType1} size={[16, 24]} />
          </Button>
        </div>

        {step === 'complete' ? (
          <PopupDefault
            configKey={completeKey}
            onClose={handleClose}
            articles={articles}
            className={variant === 'single' ? styles.submit_complete_single : undefined}
          />
        ) : (
          <FormModeContext.Provider value="popup">
            {variant === 'single' ? (
              // 단일 폼 — 메뉴 없이 Form 직접 렌더
              <Form
                type={keys[0]}
                formName={popupFormName ?? POPUP_FORM_NAME}
                leadFormNum={leadFormNum}
                leadType={POPUP_LEAD_TYPE}
                agreement={agreement}
                extraData={{
                  productType: singleMenu?.productTypeValue ? [singleMenu.productTypeValue] : [],
                }}
                className={styles.form_area}
                actionAreaClassName={styles.form_action_area}
                onSuccess={() => setStep('complete')}
              />
            ) : (
              // 탭형 / 다중선택형
              <FormConsultBody
                menus={menus}
                layout={variant === 'multi' ? 'multi' : 'tabs'}
                agreement={agreement}
                leadFormNum={leadFormNum}
                defaultMenuKey={keys[0] ?? ''}
                onClose={handleClose}
                onComplete={(meta) => {
                  setCompleteKey(meta.completeKey);
                  setStep('complete');
                }}
              />
            )}
          </FormModeContext.Provider>
        )}
      </div>
    </section>
  );
}
